import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { HttpService } from '../../game/services/HttpService';
import { SocketService } from '../../game/services/SocketService';
import { ServerMessages } from '../../game/constants/ServerMessages';
import { SocketMessage } from '../../game/types';
import { UserMessages } from '../../game/constants/UserMessages';
import { generateUUID } from '../../utils/uuid';
import Friends from '../friends.component/Friends.component';
import FriendsChat from '../friendsChat.component/FriendsChat.component';
import JoinGame from '../joinGame.component/JoinGame.component';
import './Dashboard.component.css';
import logo from '../../assets/compressed/logoMainH-min.png';

interface ActiveGame {
  id: string;
  roomId: string;
  host: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  isRunning: boolean;
  tableSeats: number;
  gameType: string;
  chips: number;
  bigBlind: number;
  smallBlind: number;
  minEntryChips: number;
  maxEntryChips: number;
  creationTime: string;
}

const Dashboard: React.FC = () => {
  const [activeGames, setActiveGames] = useState<ActiveGame[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFriends, setShowFriends] = useState(false);
  const [showFriendsChat, setShowFriendsChat] = useState(false);
  const [openChatForFriend, setOpenChatForFriend] = useState<{ id: string; name: string } | null>(null);
  const [unreadByFriendId, setUnreadByFriendId] = useState<Record<string, number>>({});
  const [focusedChatFriendId, setFocusedChatFriendId] = useState<string | null>(null);
  const [friendsChatPanelMinimized, setFriendsChatPanelMinimized] = useState(true);
  const [isDashboardConnected, setIsDashboardConnected] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [createChips, setCreateChips] = useState('1000');
  const [createSmallBlind, setCreateSmallBlind] = useState('10');
  const [createBigBlind, setCreateBigBlind] = useState('20');
  const [modalError, setModalError] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const dashboardSocketRef = useRef<SocketService | null>(null);
  const connectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null); // delay connect until after Strict Mode unmount
  const pendingCreateRoomRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  const loadActiveGamesRef = useRef<(forceRefresh?: boolean) => void>(() => {});
  const navigate = useNavigate();
  const httpService = new HttpService();
  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('userId');
  const username = localStorage.getItem('username');

  useEffect(() => {
    if (!token || !userId) {
      navigate('/login');
      return;
    }

    httpService.setToken(token);
    // Single initial load (avoids duplicate my-hosted-games request in Strict Mode)
    if (!initialLoadDoneRef.current) {
      initialLoadDoneRef.current = true;
      loadActiveGames();
    }

    // Delay connect so Strict Mode's first unmount clears this timeout; only the final mount connects (one attempt)
    if (dashboardSocketRef.current?.isConnected()) return;
    connectTimeoutRef.current = setTimeout(() => {
      connectTimeoutRef.current = null;
      connectDashboardWebSocket();
    }, 150);

    return () => {
      setIsDashboardConnected(false);
      if (connectTimeoutRef.current != null) {
        clearTimeout(connectTimeoutRef.current);
        connectTimeoutRef.current = null;
      }
      if (dashboardSocketRef.current) {
        dashboardSocketRef.current.disconnect();
        dashboardSocketRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; token/userId checked at start
  }, []);

  useEffect(() => {
    const requestUnreadCounts = () => {
      if (dashboardSocketRef.current?.isConnected()) {
        dashboardSocketRef.current.sendMessage({ type: UserMessages.GET_FRIEND_CHAT_UNREAD_COUNTS });
      }
    };
    let refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;
    const clearRefreshTimeout = () => {
      if (refreshTimeoutId != null) {
        clearTimeout(refreshTimeoutId);
        refreshTimeoutId = null;
      }
    };
    const refreshDashboardState = () => {
      if (document.visibilityState !== 'visible') return;
      clearRefreshTimeout();
      requestUnreadCounts();
      if (typeof loadActiveGamesRef.current === 'function') {
        loadActiveGamesRef.current(true);
      }
      // Delayed refresh so we get fresh server state after tab/window is fully focused
      refreshTimeoutId = setTimeout(() => {
        refreshTimeoutId = null;
        if (document.visibilityState === 'visible' && typeof loadActiveGamesRef.current === 'function') {
          loadActiveGamesRef.current(true);
        }
      }, 500);
    };
    document.addEventListener('visibilitychange', refreshDashboardState);
    window.addEventListener('focus', refreshDashboardState);
    return () => {
      document.removeEventListener('visibilitychange', refreshDashboardState);
      window.removeEventListener('focus', refreshDashboardState);
      clearRefreshTimeout();
    };
  }, []);

  const loadActiveGames = async (forceRefresh = false) => {
    try {
      const endpoint = forceRefresh
        ? `poker-tables/my-hosted-games?_=${Date.now()}`
        : 'poker-tables/my-hosted-games';
      const response = await httpService.get<any>(endpoint);
      if (response.status === 'success' && response.data) {
        setActiveGames(Array.isArray(response.data) ? response.data : []);
        setError('');
      } else {
        setError(response.message || 'Failed to load your games');
      }
    } catch (error: any) {
      console.error('Error loading your games:', error);
      setError(error.message || 'Failed to load your games');
    } finally {
      setLoading(false);
    }
  };

  loadActiveGamesRef.current = loadActiveGames;

  const connectDashboardWebSocket = async () => {
    if (!token) return;
    if (dashboardSocketRef.current?.isConnected()) return;

    try {
      if (dashboardSocketRef.current) {
        dashboardSocketRef.current.disconnect();
        dashboardSocketRef.current = null;
      }
      const socketService = new SocketService();
      dashboardSocketRef.current = socketService;
      await socketService.connect(token, 'dashboard');

      // If ref was cleared, cleanup ran (e.g. user navigated away) – don't attach handlers
      if (dashboardSocketRef.current !== socketService) {
        socketService.disconnect();
        return;
      }
      setIsDashboardConnected(true);

      socketService.onMessage(ServerMessages.FRIEND_CHAT_UNREAD_COUNTS, (message: any) => {
        const counts = message?.unreadByFriendId;
        if (counts && typeof counts === 'object') {
          setUnreadByFriendId(counts);
        }
      });

      socketService.sendMessage({ type: UserMessages.GET_FRIEND_CHAT_UNREAD_COUNTS });

      // Listen for game state updates via WebSocket
      socketService.onMessage('GAME_STATE_UPDATE', (message) => {
        console.log('GAME_STATE_UPDATE received, refreshing active games');
        loadActiveGames();
      });

      socketService.onMessage('GAME_STARTED', (message) => {
        console.log('GAME_STARTED received, refreshing active games');
        loadActiveGames();
      });

      socketService.onMessage('GAME_ENDED', (message) => {
        console.log('GAME_ENDED received, refreshing active games');
        loadActiveGames();
      });

      socketService.onMessage('ROOM_CREATED', (message: any) => {
        console.log('ROOM_CREATED received, refreshing active games');
        if (pendingCreateRoomRef.current && message?.roomId) {
          pendingCreateRoomRef.current = false;
          setShowCreateModal(false);
          setIsCreating(false);
          const lobbyUrl = `${window.location.origin}/lobby?roomId=${message.roomId}`;
          const lobbyWindow = window.open(
            lobbyUrl,
            `lobby-${message.roomId}`,
            'width=1000,height=700,resizable=yes,scrollbars=yes'
          );
          if (!lobbyWindow) {
            setError('Please allow popups to open the lobby');
          } else {
            lobbyWindow.focus();
          }
        }
        loadActiveGames();
      });

      socketService.onMessage(ServerMessages.ERROR_MESSAGE, (message: any) => {
        if (pendingCreateRoomRef.current) {
          pendingCreateRoomRef.current = false;
          const errorMsg = message.message || 'An error occurred';
          const lowerErrorMsg = errorMsg.toLowerCase();
          // Only redirect to login for actual auth/session errors, not e.g. "hosting tokens" or other errors that mention "token"
          const isAuthError =
            lowerErrorMsg.includes('unauthorized') ||
            lowerErrorMsg.includes('authentication') ||
            (lowerErrorMsg.includes('invalid') && lowerErrorMsg.includes('token') && !lowerErrorMsg.includes('hosting')) ||
            (lowerErrorMsg.includes('expired') && (lowerErrorMsg.includes('session') || lowerErrorMsg.includes('token'))) ||
            lowerErrorMsg === 'you must be logged in to send messages';
          if (isAuthError) {
            localStorage.removeItem('token');
            localStorage.removeItem('userId');
            localStorage.removeItem('username');
            localStorage.removeItem('userEmail');
            localStorage.removeItem('emailVerified');
            window.location.href = '/login';
          } else {
            setModalError(errorMsg);
            setIsCreating(false);
          }
        }
      });

      socketService.onMessage('PLAYER_JOINED', (message) => {
        console.log('PLAYER_JOINED received, refreshing active games');
        loadActiveGames();
      });

      socketService.onMessage('PLAYER_LEFT_ROOM', (message) => {
        console.log('PLAYER_LEFT_ROOM received, refreshing active games');
        loadActiveGames();
      });

      socketService.onMessage(ServerMessages.ROOM_DELETED, (message: SocketMessage) => {
        const roomId = message?.roomId as string | undefined;
        if (roomId) {
          setActiveGames((prev) => prev.filter((g) => g.roomId !== roomId));
        }
      });

      socketService.onMessage(ServerMessages.FRIEND_CHAT_MESSAGE, (msg: any) => {
        const myId = localStorage.getItem('userId');
        const senderId = msg?.senderId as string | undefined;
        if (myId && senderId && senderId !== myId) {
          setUnreadByFriendId((prev) => ({ ...prev, [senderId]: (prev[senderId] || 0) + 1 }));
        }
      });

      console.log('Dashboard WebSocket connected');
    } catch (error) {
      console.error('Failed to connect dashboard WebSocket:', error);
    }
  };

  const handleOpenGame = (roomId: string) => {
    // Open game in a new secure window
    const gameUrl = `${window.location.origin}/game?roomId=${roomId}`;
    const gameWindow = window.open(
      gameUrl,
      `game-${roomId}`,
      'width=1200,height=800,resizable=yes,scrollbars=yes'
    );

    if (!gameWindow) {
      setError('Please allow popups to open games');
      return;
    }

    // Focus the new window
    gameWindow.focus();
  };

  const handleOpenCreateModal = () => {
    setShowCreateModal(true);
    setModalError('');
    setIsCreating(false);
  };

  const handleOpenJoinModal = () => {
    setShowJoinModal(true);
  };

  const handleCloseCreateModal = () => {
    if (isCreating) return;
    pendingCreateRoomRef.current = false;
    setShowCreateModal(false);
    setModalError('');
    setIsCreating(false);
  };

  const openLobbyForRoom = (roomId: string, runningGame?: boolean) => {
    const lobbyUrl = `${window.location.origin}/lobby?roomId=${roomId}${runningGame ? '&observeLobby=1' : ''}`;
    const lobbyWindow = window.open(
      lobbyUrl,
      `lobby-${roomId}`,
      'width=1000,height=700,resizable=yes,scrollbars=yes'
    );
    if (!lobbyWindow) {
      setError('Please allow popups to open the lobby');
    } else {
      lobbyWindow.focus();
    }
  };

  /** Open game table in observer mode (player can then sit from an empty seat). */
  const openTableForRoom = (roomId: string) => {
    const gameUrl = `${window.location.origin}/game?roomId=${roomId}&observeOnly=1`;
    const w = window.open(gameUrl, `game-${roomId}`, 'width=1200,height=800,resizable=yes,scrollbars=yes');
    if (!w) {
      setError('Please allow popups to open the table');
    } else {
      w.focus();
    }
  };

  const handleCreateGame = async () => {
    // Check if email is verified
    const emailVerified = localStorage.getItem('emailVerified') === 'true';
    if (!emailVerified) {
      navigate(`/verify-email?returnTo=/dashboard`);
      return;
    }

    setModalError('');

    // Validate inputs
    const chips = parseInt(createChips);
    const small = parseInt(createSmallBlind);
    const big = parseInt(createBigBlind);

    if (chips < 10 || chips > 100000) {
      setModalError('Chips amount must be between 10 and 100,000');
      return;
    }

    if (small >= chips || big >= chips) {
      setModalError('Blind amounts must be less than total chips');
      return;
    }

    if (small >= big) {
      setModalError('Small blind must be less than big blind');
      return;
    }

    setIsCreating(true);
    setModalError('');

    try {
      console.log('dashboardSocketRef.current', dashboardSocketRef.current);
      const socket = dashboardSocketRef.current;
      console.log('socket', socket);
      console.log('socket.isConnected()', socket?.isConnected());
      if (!socket || !socket.isConnected()) {
        setModalError('Not connected to server. Please refresh and try again.');
        setIsCreating(false);
        return;
      }

      pendingCreateRoomRef.current = true;
      const newRoomId = generateUUID();
      socket.sendMessage({
        type: UserMessages.CREATE_ROOM,
        roomId: newRoomId,
        playerId: userId || '',
        chips: String(chips),
        smallBlind: String(small),
        bigBlind: String(big),
        token: token || '',
      });
    } catch (error: any) {
      console.error('Failed to create room:', error);
      pendingCreateRoomRef.current = false;
      setModalError(error.message || 'Failed to create room. Please try again.');
      setIsCreating(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('username');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('emailVerified');
    localStorage.removeItem('currentRoomId');
    if (dashboardSocketRef.current) {
      dashboardSocketRef.current.disconnect();
    }
    navigate('/login');
  };

  const getGameTypeLabel = (gameType: string) => {
    const labels: Record<string, string> = {
      'TXS_NL': 'Texas Hold\'em No Limit',
      'TXS_LM': 'Texas Hold\'em Limit',
      'PLO_4': 'Pot Limit Omaha 4',
      'PLO_5': 'Pot Limit Omaha 5',
    };
    return labels[gameType] || gameType;
  };

  const getHostName = (host: any) => {
    return `${host.firstName || ''} ${host.lastName || ''}`.trim() || host.email;
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="dashboard-title">
          <img className="header-logo" src={logo} alt="Wild Chips Poker" onClick={() => navigate('/')} />
          <h1>Dashboard</h1>
          <div className="user-info">
            <span>Welcome, {username || 'Player'}</span>
          </div>
        </div>
        <div className="dashboard-actions">
          <button onClick={() => setShowFriends(true)} className="btn-primary">
            Friends
          </button>
          <button onClick={() => setShowFriendsChat(true)} className="btn-primary dashboard-btn-friends-chat">
            Friends Chat
            {(() => {
              const total = Object.values(unreadByFriendId).reduce((a, b) => a + b, 0);
              const displayTotal =
                showFriendsChat && !friendsChatPanelMinimized && focusedChatFriendId
                  ? total - (unreadByFriendId[focusedChatFriendId] ?? 0)
                  : total;
              return displayTotal > 0 ? (
                <span className="dashboard-chat-badge" title="Unread messages">
                  {displayTotal > 99 ? '99+' : displayTotal}
                </span>
              ) : null;
            })()}
          </button>
          <button onClick={handleOpenCreateModal} className="btn-primary">
            Create Game
          </button>
          <button onClick={handleOpenJoinModal} className="btn-primary">
            Join Game
          </button>
          <button onClick={() => navigate('/settings')} className="btn-secondary">
            Settings
          </button>
          <button onClick={handleLogout} className="btn-secondary">
            Logout
          </button>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="active-games-section">
          <h2>Your Active Games</h2>
          {error && <div className="error-message">{error}</div>}
          
          {loading ? (
            <div className="loading-message">Loading active games...</div>
          ) : activeGames.length === 0 ? (
            <div className="empty-message">
              <p>You don't have any active games.</p>
              <button onClick={handleOpenCreateModal} className="btn-primary">
                Create Game
              </button>
            </div>
          ) : (
            <div className="games-grid">
              {activeGames.map((game) => (
                <div key={game.id} className="game-card">
                  <div className="game-card-header">
                    <span className={`game-status ${game.isRunning ? 'running' : 'waiting'}`}>
                      {game.isRunning ? '● Running' : '● Waiting'}
                    </span>
                    <span className="game-type">{getGameTypeLabel(game.gameType)}</span>
                  </div>
                  <div className="game-card-body">
                    <div className="game-info-row">
                      <span className="info-label">Room ID:</span>
                      <span className="info-value room-id">{game.roomId}</span>
                    </div>
                    <div className="game-info-row">
                      <span className="info-label">Host:</span>
                      <span className="info-value">{getHostName(game.host)}</span>
                    </div>
                    <div className="game-info-row">
                      <span className="info-label">Blinds:</span>
                      <span className="info-value">${game.smallBlind}/${game.bigBlind}</span>
                    </div>
                    <div className="game-info-row">
                      <span className="info-label">Buy-in:</span>
                      <span className="info-value">${game.minEntryChips} - ${game.maxEntryChips}</span>
                    </div>
                    <div className="game-info-row">
                      <span className="info-label">Seats:</span>
                      <span className="info-value">{game.tableSeats}</span>
                    </div>
                  </div>
                  <div className="game-card-footer">
                    {game.isRunning && userId !== game.host?.id && (
                      <button
                        onClick={() => openTableForRoom(game.roomId)}
                        className="btn-open-game"
                        title="Open game table as observer (watch only)"
                      >
                        Open table
                      </button>
                    )}
                    <button
                      onClick={() => openLobbyForRoom(game.roomId, game.isRunning)}
                      className={game.isRunning ? 'btn-open-lobby' : 'btn-open-game'}
                      title={game.isRunning ? 'Open lobby in a new window' : 'Open lobby'}
                    >
                      Open lobby
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {showFriendsChat && (
        <FriendsChat
          onClose={() => setShowFriendsChat(false)}
          socketRef={dashboardSocketRef}
          isConnected={isDashboardConnected}
          unreadByFriendId={unreadByFriendId}
          setUnreadByFriendId={setUnreadByFriendId}
          openChatForFriend={openChatForFriend}
          onOpenChatHandled={() => setOpenChatForFriend(null)}
          onPanelStateChange={(focusedFriendId, panelMinimized) => {
            setFocusedChatFriendId(focusedFriendId);
            setFriendsChatPanelMinimized(panelMinimized);
          }}
        />
      )}
      {showFriends && (
        <Friends
          onClose={() => setShowFriends(false)}
          onOpenChat={(friendId, friendName) => {
            setShowFriends(false);
            setOpenChatForFriend({ id: friendId, name: friendName });
            setShowFriendsChat(true);
          }}
          onJoinFriendGame={(roomId) => {
            setShowFriends(false);
            // Open lobby in new window when joining a friend's game
            const lobbyUrl = `${window.location.origin}/lobby?roomId=${roomId}`;
            const lobbyWindow = window.open(
              lobbyUrl,
              `lobby-${roomId}`,
              'width=1000,height=700,resizable=yes,scrollbars=yes'
            );
            if (!lobbyWindow) {
              setError('Please allow popups to open the lobby');
            } else {
              lobbyWindow.focus();
            }
          }}
        />
      )}
      {showJoinModal && (
        <JoinGame
          onClose={() => setShowJoinModal(false)}
          onJoinGame={(roomId) => {
            setShowJoinModal(false);
            openLobbyForRoom(roomId);
          }}
          onOpenTable={openTableForRoom}
          onOpenLobby={(roomId, isRunning) => {
            setShowJoinModal(false);
            openLobbyForRoom(roomId, isRunning);
          }}
        />
      )}
      {showCreateModal && (
        <div className="modal-overlay" onClick={handleCloseCreateModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Game</h2>
              <button
                type="button"
                className="modal-close"
                onClick={handleCloseCreateModal}
                disabled={isCreating}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {modalError && <div className="modal-error">{modalError}</div>}
              <div className="modal-form">
                <div className="form-group">
                  <label>Chips Amount (10 - 100,000)</label>
                  <input
                    type="number"
                    value={createChips}
                    onChange={(e) => setCreateChips(e.target.value)}
                    min="10"
                    max="100000"
                    disabled={isCreating}
                  />
                </div>
                <div className="form-group">
                  <label>Small Blind</label>
                  <input
                    type="number"
                    value={createSmallBlind}
                    onChange={(e) => setCreateSmallBlind(e.target.value)}
                    min="1"
                    disabled={isCreating}
                  />
                </div>
                <div className="form-group">
                  <label>Big Blind</label>
                  <input
                    type="number"
                    value={createBigBlind}
                    onChange={(e) => setCreateBigBlind(e.target.value)}
                    min="1"
                    disabled={isCreating}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleCreateGame}
                  className="btn-primary"
                  disabled={isCreating}
                >
                  {isCreating ? 'Creating...' : 'Create Game'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;

